import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Apple, Play, ChevronDown, Crown, Loader2 } from "lucide-react";
import PhoneMockup from "@/components/PhoneMockup";
import Countdown from "@/components/Countdown";
import InstallApp from "@/components/InstallApp";
import LiveStats from "@/components/LiveStats";
import { scrollToSection } from "@/lib/scroll";
import { getConfig } from "@/lib/config";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lines = [
  { text: "No-shows get", accent: " banned.", accentClass: "text-red-500" },
  { text: "Elite guards", accent: " get paid.", accentClass: "text-amber-500" },
];

const openStore = async (kind) => {
  const cfg = await getConfig();
  const url = kind === "ios" ? cfg.app_store_url : cfg.play_store_url;
  if (url) window.open(url, "_blank", "noopener");
  else toast("Store links coming soon — use \"Install app\" to get GuardGig28 on your phone right now.");
};

export default function Hero() {
  const ref = useRef(null);
  const [payBusy, setPayBusy] = useState(false);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });

  const claimPass = async () => {
    setPayBusy(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lookup_key: "founding_pass", origin_url: window.location.origin, user_id: "" }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error(data.detail || "Checkout failed to start — try the Founding Pass section below.");
    } catch {
      toast.error("Checkout failed to start — try the Founding Pass section below.");
    } finally {
      setPayBusy(false);
    }
  };

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="bg-grid-tactical relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28"
    >
      <div className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full bg-amber-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-red-500/5 blur-[120px]" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/5 px-4 py-1.5"
          >
            <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span data-testid="hero-badge" className="font-mono-tag text-[10px] text-amber-500">GuardGig28 · Discipline Engine</span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display mt-8 text-5xl font-black uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-[5.5rem]">
            {lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 + i * 0.14 }}
                >
                  {line.text}
                  <span className={line.accentClass}>{line.accent}</span>
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            GuardGig28 is live. SIA-licensed guards and venues, matched in minutes.
            Claim one of the 500 Founding Passes — £39 once, lifetime membership, no
            monthly fee ever. One rule keeps it honest: accept a shift and fail to show
            up, and you're permanently banned. Serious about work. Fair about everything else.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-claim-pass-btn"
              onClick={claimPass}
              disabled={payBusy}
              className="flex items-center gap-3 rounded-md bg-amber-500 px-7 py-4 font-display text-lg font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 hover:shadow-[0_0_35px_rgba(245,158,11,0.4)] disabled:opacity-60"
            >
              {payBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Crown className="h-5 w-5" />}
              Claim your pass — £39
            </button>
            <button
              data-testid="hero-browse-btn"
              onClick={() => scrollToSection("shifts")}
              className="flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-6 py-4 font-display text-base font-bold uppercase text-slate-50 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-500/40"
            >
              Browse shifts free
            </button>
            <button
              data-testid="hero-rules-btn"
              onClick={() => scrollToSection("rules")}
              className="font-mono-tag text-[11px] text-slate-400 underline decoration-amber-500/50 underline-offset-8 transition-colors hover:text-amber-500"
            >
              Read the rules first
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-appstore-btn"
              onClick={() => openStore("ios")}
              className="flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-left text-slate-300 transition-colors hover:border-amber-500/40 hover:text-slate-50"
            >
              <Apple className="h-5 w-5" fill="currentColor" />
              <span>
                <span className="block text-[8px] uppercase tracking-wider opacity-60">Download on the</span>
                <span className="block font-display text-sm font-bold leading-none">App Store</span>
              </span>
            </button>
            <button
              data-testid="hero-googleplay-btn"
              onClick={() => openStore("android")}
              className="flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-left text-slate-300 transition-colors hover:border-amber-500/40 hover:text-slate-50"
            >
              <Play className="h-4 w-4" fill="currentColor" />
              <span>
                <span className="block text-[8px] uppercase tracking-wider opacity-60">Get it on</span>
                <span className="block font-display text-sm font-bold leading-none">Google Play</span>
              </span>
            </button>
            <InstallApp />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-8"
          >
            <Countdown />
          </motion.div>

          <LiveStats />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
          style={{ perspective: 1200 }}
        >
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
            <PhoneMockup />
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToSection("rules")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-slate-600 transition-colors hover:text-amber-500 md:flex"
      >
        <span className="font-mono-tag text-[9px]">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
